import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtInt, fmtNum } from "../lib/api";
import PageHeader from "../components/PageHeader";
import KpiCard from "../components/KpiCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Filter, RefreshCw } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from "recharts";
import { toast } from "sonner";

const METRICS = [
  { key: "spend", label: "Spend (£)", color: "#0ea5e9", money: true },
  { key: "clicks", label: "Clicks", color: "#16a34a" },
  { key: "registrations", label: "Registrations", color: "#dc2626" },
];

export default function Trends() {
  const [days, setDays] = useState("30");
  const [startDate, setStart] = useState("");
  const [endDate, setEnd] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [city, setCity] = useState("");
  const [enabledMetrics, setEnabledMetrics] = useState({
    spend: true, clicks: true, registrations: true,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data.accounts || []))
      .catch(() => toast.error("Failed to load accounts"));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (days === "custom") {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      } else { params.days = days; }
      if (accountIds.length) params.account_ids = accountIds.join(",");
      if (city.trim()) params.city = city.trim();
      const r = await api.get("/trends/daily", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load trends", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, startDate, endDate, accountIds.join(","), city]);

  const chartData = data?.series || [];
  const totals = data?.totals || {};

  const accountLabel = useMemo(() => {
    if (!accountIds.length) return "All accounts";
    if (accountIds.length === 1) {
      const a = accounts.find((x) => x.account_id === accountIds[0]);
      return a?.name || accountIds[0];
    }
    return `${accountIds.length} accounts`;
  }, [accountIds, accounts]);

  return (
    <div data-testid="trends-page" className="fade-in">
      <PageHeader
        eyebrow="Daily Trends"
        title="Daily spend, clicks & registrations"
        description="Daily store-wide trend lines from ThoughtMetric. Filter the totals by accounts and city (substring match on campaign name)."
        actions={
          <Button onClick={load} disabled={loading} data-testid="trends-refresh-btn"
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-wider text-xs">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 space-y-6">
        {/* Filters */}
        <div className="bg-white border border-zinc-200 px-5 py-4 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <Label className="data-label">Range</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="trends-range" className="rounded-none border-zinc-300 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {days === "custom" && (
            <>
              <div>
                <Label className="data-label">Start</Label>
                <Input type="date" value={startDate} onChange={(e) => setStart(e.target.value)}
                  className="rounded-none border-zinc-300 mt-1 font-mono text-sm" data-testid="trends-start" />
              </div>
              <div>
                <Label className="data-label">End</Label>
                <Input type="date" value={endDate} onChange={(e) => setEnd(e.target.value)}
                  className="rounded-none border-zinc-300 mt-1 font-mono text-sm" data-testid="trends-end" />
              </div>
            </>
          )}
          <div>
            <Label className="data-label">Accounts</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="trends-accounts-trigger"
                  className="w-full mt-1 rounded-none border-zinc-300 font-mono text-xs justify-between">
                  <span className="truncate">{accountLabel}</span>
                  <Filter className="w-3 h-3 ml-2 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 max-h-80 overflow-auto rounded-none p-0 border-zinc-300">
                <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
                  <div className="data-label">Filter accounts</div>
                  <button onClick={() => setAccountIds([])} className="text-[10px] font-mono uppercase hover:underline">clear</button>
                </div>
                <ul className="text-sm">
                  {accounts.map((a) => {
                    const sel = accountIds.includes(a.account_id);
                    return (
                      <li key={a.account_id}>
                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`trends-acct-${a.account_id}`}
                            checked={sel}
                            onChange={() => setAccountIds((ids) => sel ? ids.filter((x) => x !== a.account_id) : [...ids, a.account_id])}
                            className="accent-zinc-900"
                          />
                          <span className="font-mono text-xs truncate flex-1" title={a.name}>{a.name}</span>
                          <span className="text-[10px] font-mono uppercase text-zinc-500">{a.network}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="data-label">City filter</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. LONDON, SINGAPORE"
              className="rounded-none border-zinc-300 mt-1 font-mono text-sm" data-testid="trends-city" />
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard testid="kpi-total-spend" label="Total Spend" value={fmtMoney(totals.spend || 0)} />
          <KpiCard testid="kpi-total-clicks" label="Total Clicks" value={fmtInt(totals.clicks || 0)} />
          <KpiCard testid="kpi-total-regs" label="Registrations" value={fmtInt(totals.registrations || 0)} />
          <KpiCard testid="kpi-daily-budget" label="Current Daily Budget"
            value={fmtMoney(data?.current_daily_budget_total || 0)}
            sub={`${totals.matched_campaigns || 0} campaigns`} />
        </div>

        {/* Chart */}
        <div className="bg-white border border-zinc-200 p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="data-label">Daily trend</div>
            <div className="flex items-center gap-3">
              {METRICS.map((m) => (
                <label key={m.key} className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`trends-metric-${m.key}`}
                    checked={!!enabledMetrics[m.key]}
                    onChange={() => setEnabledMetrics((e) => ({ ...e, [m.key]: !e[m.key] }))}
                    className="accent-zinc-900"
                  />
                  <span style={{ color: m.color }}>■</span> {m.label}
                </label>
              ))}
            </div>
          </div>
          {chartData.length === 0 && !loading && (
            <div className="py-16 text-center text-zinc-500 font-mono text-sm">No data for this range.</div>
          )}
          {chartData.length > 0 && (() => {
            const budget = data?.current_daily_budget_total || 0;
            // The series already carries per-day `daily_budget` from the backend
            // (reconstructed from budget_changes); if missing, fall back to current.
            const withBudget = chartData.map((d) => ({
              ...d,
              budget: typeof d.daily_budget === "number" ? d.daily_budget : budget,
            }));
            const maxSpend = Math.max(0, ...chartData.map((d) => Number(d.spend) || 0));
            const maxBudget = Math.max(0, ...withBudget.map((d) => Number(d.budget) || 0));
            const leftMax = Math.max(maxSpend, maxBudget * 1.15, 1);
            return (
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                <LineChart data={withBudget} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11, fontFamily: "monospace" }}
                    tickFormatter={(v) => `£${v}`}
                    domain={[0, leftMax]}
                  />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                  <Tooltip
                    contentStyle={{ background: "white", border: "1px solid #d4d4d8", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }}
                    formatter={(value, name) => {
                      const m = METRICS.find((x) => x.label === name);
                      if (m?.money) return [`£${Number(value).toLocaleString()}`, name];
                      return [Number(value).toLocaleString(), name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                  {(budget > 0 || withBudget.some((d) => d.budget > 0)) && (
                    <Line
                      yAxisId="left"
                      type="stepAfter"
                      dataKey="budget"
                      name="Daily budget"
                      stroke="#9333ea"
                      strokeDasharray="6 4"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  )}
                  {METRICS.map((m) => enabledMetrics[m.key] ? (
                    <Line
                      key={m.key}
                      yAxisId={m.key === "spend" ? "left" : "right"}
                      type="monotone"
                      dataKey={m.key}
                      name={m.label}
                      stroke={m.color}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                    />
                  ) : null)}
                </LineChart>
              </ResponsiveContainer>
            </div>
            );
          })()}
          {data?.note && (
            <div className="mt-3 text-[10px] font-mono text-zinc-500 italic">{data.note}</div>
          )}
        </div>
      </div>
    </div>
  );
}
